// imageService.js - 图像处理相关的服务
import axios from 'axios';

/**
 * 图片压缩函数
 * @param {File} file - 原始图片文件
 * @param {number} quality - 压缩质量 (0-1)
 * @returns {Promise<Blob>} - 压缩后的图片Blob对象
 */
export const compressImage = async (file, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('图片压缩失败'));
            }
          },
          file.type,
          quality
        );
      };
      img.onerror = () => reject(new Error('图片加载失败'));
    };
    reader.onerror = () => reject(new Error('文件读取失败'));
  });
};

/**
 * 创建Canvas元素
 * @param {number} width - 画布宽度
 * @param {number} height - 画布高度
 * @returns {HTMLCanvasElement} - Canvas元素
 */
export const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

/**
 * 加载图片为HTML Image元素
 * @param {string} src - 图片URL
 * @returns {Promise<HTMLImageElement>} - 加载好的图片元素
 */
export const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // 使用小写解决浏览器兼容性问题
    img.onload = () => resolve(img);
    img.onerror = (e) => {
      console.error('图片加载失败:', e, src);
      reject(e);
    };
    img.src = src;
  });
};

/**
 * 添加水印到图片
 * @param {string} imageUrl - 原始图片URL
 * @param {Object} watermarkSettings - 水印设置
 * @returns {Promise<string>} - 添加水印后的图片URL
 */
export const addWatermark = async (imageUrl, watermarkSettings) => {
  try {
    // 加载图片
    const img = await loadImage(imageUrl);
    
    // 创建Canvas
    const canvas = createCanvas(img.width, img.height);
    const ctx = canvas.getContext('2d');
    
    // 绘制原始图片
    ctx.drawImage(img, 0, 0);
    
    if (watermarkSettings.enabled) {
      // 设置水印样式
      ctx.font = `${watermarkSettings.fontSize}px Arial`;
      ctx.fillStyle = watermarkSettings.color;
      ctx.globalAlpha = watermarkSettings.opacity;
      
      // 计算水印位置
      let x, y;
      const text = watermarkSettings.text;
      const textWidth = ctx.measureText(text).width;
      
      // 根据位置设置水印坐标
      switch(watermarkSettings.position) {
        case 'topLeft':
          x = 20;
          y = 40;
          break;
        case 'topRight':
          x = img.width - textWidth - 20;
          y = 40;
          break;
        case 'bottomLeft':
          x = 20;
          y = img.height - 20;
          break;
        case 'bottomRight':
          x = img.width - textWidth - 20;
          y = img.height - 20;
          break;
        default: // center
          x = (img.width - textWidth) / 2;
          y = img.height / 2;
      }
      
      // 绘制水印文字
      ctx.save();
      if (watermarkSettings.angle !== 0) {
        // 旋转水印
        ctx.translate(x + textWidth/2, y);
        ctx.rotate(watermarkSettings.angle * Math.PI / 180);
        ctx.fillText(text, -textWidth/2, 0);
      } else {
        ctx.fillText(text, x, y);
      }
      ctx.restore();
      
      // 如果需要重复水印
      if (watermarkSettings.repeat) {
        try {
          const patternCanvas = createCanvas(textWidth * 3, watermarkSettings.fontSize * 3);
          const patternCtx = patternCanvas.getContext('2d');
          
          patternCtx.font = ctx.font;
          patternCtx.fillStyle = watermarkSettings.color;
          patternCtx.globalAlpha = watermarkSettings.opacity;
          patternCtx.translate(patternCanvas.width/2, patternCanvas.height/2);
          patternCtx.rotate(watermarkSettings.angle * Math.PI / 180);
          patternCtx.fillText(text, -textWidth/2, 0);
          
          // 创建水印图案
          const pattern = ctx.createPattern(patternCanvas, 'repeat');
          if (pattern) {
            ctx.globalAlpha = watermarkSettings.opacity / 2; // 降低重复水印的透明度
            ctx.fillStyle = pattern;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        } catch (patternError) {
          console.error('创建重复水印图案出错:', patternError);
          // 如果创建图案出错，就用简单的方式绘制多个水印
          for(let i = 0; i < img.width; i += textWidth * 4) {
            for(let j = 0; j < img.height; j += watermarkSettings.fontSize * 4) {
              ctx.globalAlpha = watermarkSettings.opacity / 3;
              ctx.fillText(text, i, j);
            }
          }
        }
      }
    }
    
    // 返回处理后的图片URL
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    console.error('添加水印失败:', error);
    return imageUrl; // 出错时返回原图
  }
};

/**
 * 生成九宫格图片
 * @param {Array<HTMLImageElement>} images - 图片元素数组
 * @returns {Promise<string>} - 生成的九宫格图片URL
 */
export const generateGrid = async (images) => {
  try {
    const canvas = createCanvas(1000, 1000);
    const ctx = canvas.getContext('2d');
    
    // 填充白色背景
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 计算每个图片的大小和位置
    const imgSize = 500;  // 2x2 布局每个图片占500px
    
    // 绘制图片到九宫格
    images.forEach((img, index) => {
      if (index < 4) {  // 最多支持4张图片
        const row = Math.floor(index / 2);
        const col = index % 2;
        const x = col * imgSize;
        const y = row * imgSize;
        
        ctx.drawImage(img, x, y, imgSize, imgSize);
      }
    });
    
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    console.error('生成九宫格失败:', error);
    return null;
  }
};

/**
 * 上传图片到服务器
 * @param {File|Blob} file - 要上传的文件
 * @param {string} fileName - 文件名
 * @returns {Promise<Object>} - 上传结果
 */
export const uploadImage = async (file, fileName) => {
  const formData = new FormData();
  formData.append('file', file, fileName);
  
  try {
    const response = await axios.post('/api/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('上传图片失败:', error);
    throw error;
  }
};

/**
 * 生成模特展示图片
 * @param {string} imageUrl - 原始图片URL
 * @param {string} prompt - 生成提示
 * @returns {Promise<string>} - 生成的模特图片URL
 */
export const generateModelImage = async (imageUrl, prompt) => {
  try {
    const response = await axios.post('/api/generate-model', {
      imageUrl,
      prompt,
    });
    return response.data.imageUrl;
  } catch (error) {
    console.error('生成模特图片失败:', error);
    throw error;
  }
};
